import type { CSSProperties, JSX } from 'react';
import { TextField } from '../../../../_components/TextField.js';
import type { Palette } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';
import { ChoiceRow } from './ChoiceRow.js';

interface MultipleChoiceFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: string[]) => void;
}

const OTHER_PREFIX = 'other:';

// Multi-choice with optional "Other" row. Other entries live alongside
// preset values in the array, prefixed with `other:` so survey authors can
// pick them out. Toggling Other on adds a placeholder `other:`; the user
// then types their text which replaces it in place.
export function MultipleChoiceField({ palette, question, value, onChange }: MultipleChoiceFieldProps): JSX.Element {
  const options = question.settings?.options ?? [];
  const allowOther = Boolean(question.settings?.allowOther);
  const current = Array.isArray(value) ? (value as string[]) : [];

  const toggle = (optionValue: string): void => {
    const next = current.includes(optionValue)
      ? current.filter((entry) => entry !== optionValue)
      : [...current, optionValue];
    onChange(next);
  };

  const otherEntry = current.find((entry) => entry.startsWith(OTHER_PREFIX));
  const otherSelected = Boolean(otherEntry);
  const otherText = otherEntry ? otherEntry.slice(OTHER_PREFIX.length) : '';

  const setOther = (text: string): void => {
    const withoutOther = current.filter((entry) => !entry.startsWith(OTHER_PREFIX));
    onChange([...withoutOther, `${OTHER_PREFIX}${text}`]);
  };

  const toggleOther = (): void => {
    if (otherSelected) {
      onChange(current.filter((entry) => !entry.startsWith(OTHER_PREFIX)));
    } else {
      onChange([...current, OTHER_PREFIX]);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {options.map((option) => (
        <ChoiceRow
          key={option.value}
          option={option}
          selected={current.includes(option.value)}
          onClick={() => toggle(option.value)}
          palette={palette}
          multi
        />
      ))}
      {allowOther && (
        <>
          <ChoiceRow
            option={{ label: 'Other…', value: OTHER_PREFIX }}
            selected={otherSelected}
            onClick={toggleOther}
            palette={palette}
            multi
          />
          {otherSelected && (
            <div style={otherInputWrap()}>
              <TextField
                palette={palette}
                value={otherText}
                onChange={setOther}
                placeholder="Tell us more"
                ariaLabel="Other"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function otherInputWrap(): CSSProperties {
  return { padding: '4px 30px 8px' };
}
